import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { throwError, Observable, BehaviorSubject, map, catchError } from "rxjs";
// Angular + external
import { Injectable } from "@angular/core";
//BFX
import { BfxUIConfiguration, BfxInstallConfiguration, SessionType } from "./model/config.models";
import { HttpClient } from "@angular/common/http";
import { NativeDesktopSupportShared } from "../../native-desktop/NativeDesktopSupportShared";

@Injectable()
export class BfxConfigService {
  private control = {
    assets: {
      uiConfigFile: "../../assets/JSON/ui_configuration.json",
      installConfigFile: "../../assets/JSON/install_configuration.json",
    },
    config: {
      uiAll: null, // BfxUIConfiguration[]
      uiActive: new BfxUIConfiguration(
        "Initial UI Temp Config",
        "temp",
        "BarracudaFX",
        "0",
        {
          debug: false,
          homeRoute: "main",
          showDevMenu: false,
          exportLimit: 30000,
          openExternalInTab: false,
          oboEnabled: false,
          loginTimeout: 10000,
          appNotifSoundThrottleInterval: 15000,
        },
        {
          loadDelay: 0,
          useDummyAuth: false,
          showLoadingLog: true,
        },
        {
          sessionType: SessionType.VISIBILITY,
          sessionTimeout: 900000,
          sessionDuration: 3600000,
          showNotification: false,
          notificationOffset: 10000,
          autoReconnectionTimeout: 60000,
        },
      ),
      install: new BfxInstallConfiguration(
        "Initial INSTALL Temp Config",
        "temp",
        "BarracudaFX",
        "0",
        "",
        "",
        "",
      ),
    },
  };

  /***********************************/
  /* Subscriptions */

  private uiConfigSource = new BehaviorSubject<BfxUIConfiguration>(this.control.config.uiActive);
  uiConfig$ = this.uiConfigSource.asObservable();

  private installConfigSource = new BehaviorSubject<BfxInstallConfiguration>(
    this.control.config.install,
  );
  installConfig$ = this.installConfigSource.asObservable();

  constructor(private http: HttpClient) {
    this.initializeUIConfig();
    this.initializeInstallConfig();
  }

  /***********************************/
  /* Initialization */
  private initializeUIConfig(): Promise<BfxUIConfiguration> {
    return new Promise((resolve, reject) => {
      this.loadUIConfig().subscribe(
        (configs) => {
          this.control.config.uiAll = configs;
          this.control.config.uiActive = this.control.config.uiAll.find(
            (c) => c.config_type && c.config_type === "system",
          );

          this.control.config.uiActive.general.debug
            ? logger.out.info(
                "UI Configuration =>  ALL: ",
                this.control.config.uiAll,
                " | Active: ",
                this.control.config.uiActive,
              )
            : null;

          this.notifyUIConfigUpdate(this.control.config.uiActive);
          resolve(this.control.config.uiActive);
        },
        (err) => {
          logger.out.error("ERROR: ConfigService.initializeUIConfig():", err);
          reject(err);
        },
      );
    });
  }

  private initializeInstallConfig(): Promise<BfxInstallConfiguration> {
    return new Promise((resolve, reject) => {
      this.loadInstallConfig().subscribe(
        (config) => {
          this.processConfigDownloadLink(config);
          this.control.config.install = config;
          this.control.config.uiActive.general.debug
            ? logger.out.info("INSTALL Configuration => Install: ", this.control.config.install)
            : null;

          this.notifyInstallConfigUpdate(this.control.config.install);
          resolve(this.control.config.install);
        },
        (err) => {
          logger.out.error("ERROR: ConfigService.initializeInstallConfig():", err);
          reject(err);
        },
      );
    });
  }

  private processConfigDownloadLink(config: any): void {
    if (
      config != null &&
      !NativeDesktopSupportShared.isNativeDesktopContainer() &&
      config.download_text != null &&
      config.download_text.trim() != ""
    ) {
      let descriptorURL: string = encodeURIComponent(
        window.location.href.substr(
          0,
          window.location.href.indexOf("/", window.location.protocol.length + 2) + 1,
        ) + "app.json",
      );
      let downloadLink: string = config.download_text.split("[hostname]").join(descriptorURL);
      config.download_text = downloadLink;
    }
  }

  /***********************************/
  /* UI Config */

  public getUIConfig(): Promise<BfxUIConfiguration> {
    return new Promise((resolve, reject) => {
      try {
        if (this.control.config.uiActive && this.control.config.uiActive.config_type !== "temp") {
          resolve(this.control.config.uiActive);
        } else {
          resolve(this.initializeUIConfig());
        }
      } catch (err) {
        logger.out.error("EXCEPTION: ConfigService.getConfig():", err);
        reject(err);
      }
    });
  }

  public setUIConfig(config: BfxUIConfiguration): void {
    if (config.config_type === "system" || config.config_type === "client") {
      this.control.config.uiActive = config;
      this.notifyUIConfigUpdate(this.control.config.uiActive);
    }
  }

  public reloadUIConfig(): Promise<BfxUIConfiguration> {
    return this.initializeUIConfig();
  }

  private loadUIConfig(): Observable<BfxUIConfiguration[]> {
    let result = this.http.get(this.control.assets.uiConfigFile).pipe(
      map((response) => <BfxUIConfiguration[]>response),
      catchError((error: any) => throwError(() => error.json().error || new Error("Server error"))),
    );
    return result;
  }

  private notifyUIConfigUpdate(uiConfigUpdate: BfxUIConfiguration): void {
    this.uiConfigSource.next(uiConfigUpdate);
  }

  /***********************************/
  /* INSTALL Config */
  public getInstallConfig(): Promise<BfxInstallConfiguration> {
    return new Promise((resolve, reject) => {
      try {
        if (this.control.config.install && this.control.config.install.config_type !== "temp") {
          resolve(this.control.config.install);
        } else {
          resolve(this.initializeInstallConfig());
        }
      } catch (err) {
        logger.out.error("EXCEPTION: ConfigService.getConfig():", err);
        reject(err);
      }
    });
  }

  public setInstallConfig(config: BfxInstallConfiguration): void {
    if (config.config_type === "install") {
      this.control.config.install = config;
      this.notifyInstallConfigUpdate(this.control.config.install);
    }
  }

  public reloadInstallConfig(): Promise<BfxInstallConfiguration> {
    return this.initializeInstallConfig();
  }

  private loadInstallConfig(): Observable<BfxInstallConfiguration> {
    let result = this.http.get(this.control.assets.installConfigFile).pipe(
      map((response) => <BfxInstallConfiguration>response),
      catchError((error: any) => throwError(() => error.json().error || new Error("Server error"))),
    );
    return result;
  }

  private notifyInstallConfigUpdate(installConfigUpdate: BfxInstallConfiguration): void {
    this.installConfigSource.next(installConfigUpdate);
  }
}
